import { useMemo } from 'react';
import { useQuery } from 'react-query';
import api from 'api';
import {
  EmployeeFieldAccess,
  EmployeeFieldAccessRole,
  PayrollEmployeeFieldAccessRolePayload,
} from 'api/gql-utils/employeeDataSettings/types';

export const EMPLOYEE_FIELD_ACCESS_ROLES_QUERY_KEY = [
  'employee-data-settings',
  'field-access-roles',
];

const toNumericRoleId = (roleId: string | null, index: number) => {
  const parsedRoleId = Number(roleId);
  return Number.isFinite(parsedRoleId) && parsedRoleId > 0 ? parsedRoleId : index + 1;
};

const normalizeAccess = (access: EmployeeFieldAccess | null): EmployeeFieldAccess =>
  access ?? 'VIEW_ONLY';

const mapRole = (
  role: PayrollEmployeeFieldAccessRolePayload,
  index: number,
): EmployeeFieldAccessRole => {
  const defaultAccess = normalizeAccess(role.access);
  const memberCount = role.memberCount ?? 0;

  return {
    roleId: toNumericRoleId(role.id, index),
    roleKey: role.id ?? `ROLE_${index + 1}`,
    name: role.name ?? 'Role',
    roleType: 'CUSTOM',
    memberCount,
    hasMembers: memberCount > 0,
    isCurrentUserRole: false,
    isAccessLocked: Boolean(role.locked),
    lockedAccess: role.locked ? defaultAccess : null,
    defaultAccess,
  };
};

export const useEmployeeFieldAccessRoles = () => {
  const query = useQuery(EMPLOYEE_FIELD_ACCESS_ROLES_QUERY_KEY, async () => {
    const response = await api.employeeDataSettings.queries.listEmployeeFieldAccessRoles();
    return response.payrollEmployeeDataFieldAccessRoles.data;
  });

  const roles = useMemo(
    () => (query.data ?? []).map((role, index) => mapRole(role, index)),
    [query.data],
  );

  const data = useMemo(
    () => ({
      roles,
      summary: {
        totalRoles: roles.length,
        rolesWithMembers: roles.filter((role) => role.hasMembers).length,
        rolesWithoutMembers: roles.filter((role) => !role.hasMembers).length,
      },
    }),
    [roles],
  );

  return {
    ...query,
    data,
  };
};
